import { catchError } from "@opendash/core";
import { useTranslation } from "@opendash/i18n";
import Parse from "parse";
import { useCallback, useEffect, useState } from "react";
import { MetaField, StepFieldType } from "..";
import { useConfig } from "../../../_shared";
import { OpenService } from "../../../types/config";
import { StepValueType } from "../types/StepTypes";

/**
 * Gets meta fields for schedule steps.
 *
 * @param stepfields A metafield with a stored value
 * @returns
 */
export const useMetaFields = (stepfields: StepFieldType[]) => {
  const t = useTranslation();
  const config = useConfig<MetaField[]>(
    OpenService.ConfigKeys.scheduleMetaFields
  );
  const [results, setResults] = useState<StepFieldType[]>([]);

  useEffect(() => {
    if (stepfields === undefined && config.value === undefined) {
      return;
    }

    if (stepfields === undefined && config.value !== undefined) {
      setResults(
        config.value.map((metaField) => {
          return { value: undefined, metafield: metaField };
        })
      );
      return;
    }

    updateMetafields(stepfields, config.value);

    //stepfield is not undefined, set results to stepfields
    setResults(stepfields);
  }, [stepfields, config.value]);

  /**
   * Updates all meta fields in the stepFields array with the values from the metaFields array
   * directly transmutes the stepFields array
   * @param stepFields
   * @param metaFields
   */
  function updateMetafields(
    stepFields: StepFieldType[],
    metaFields: MetaField[]
  ): void {
    if (!metaFields) return;
    for (const metaField of metaFields) {
      const relatedStepField = stepFields?.find(
        (field) => field.metafield.label === metaField.label
      );
      if (!relatedStepField) continue;
      relatedStepField.metafield = metaField;
    }
  }

  /**
   * Checks if the passed value is a linked option and returns the translated value
   * @param field
   * @returns The translated value of the linked option
   */
  const getTagValue = (field: StepFieldType): Promise<string>[] => {
    if (
      field.value === undefined ||
      (Array.isArray(field.value) && field.value.length === 0)
    ) {
      const promise = new Promise<string>((resolve, reject) => {
        resolve("-");
      });

      return [promise];
    }

    const valuearray = Array.isArray(field.value) ? field.value : [field.value];

    //Call resolveLinkedOptions to get the correct label
    return valuearray.map(async (value) => {
      return await resolveLinkedOptions(value);
    });
  };

  /**
   * returns the value of a field resolved by className if needed.
   * @param field
   * @returns
   */
  const getFieldValue = (field: MetaField): StepValueType | StepValueType[] => {
    //Find field in results by label
    const foundfield = findMetaFieldInResults(field);

    //Check if foundfield is undefined and return an empty string
    if (foundfield === undefined) {
      return undefined;
    }

    return !foundfield.value ||
      (foundfield.value instanceof Array && foundfield.value.length === 0)
      ? undefined
      : foundfield.value;
  };

  /**
   * Finds a MetaField in the results array by label
   */
  const findMetaFieldInResults = useCallback((field: MetaField) => {
    return results.find((f) =>
      f !== undefined && f !== null ? f.metafield.label === field.label : false
    );
  }, []);

  /**
   * Checks if the passed value is a linked option and returns the translated value
   * @param field
   * @returns The translated value of the linked option
   */
  const resolveLinkedOptions = useCallback(async (value: StepValueType) => {
    if (!value.classname) return value.value;
    const [valueObjectError, valueObject] = await catchError(
      new Parse.Query(value.classname).get(value.value)
    );
    if (valueObjectError || valueObject === undefined) {
      return undefined;
    }

    //The value is a key of a Parse class, get the correct label
    return t("maintenance:schedule.meta.input.linked." + value.classname, {
      ...valueObject.attributes,
    });
  }, []);

  const stepFieldHasValue = useCallback((stepfield: StepFieldType) => {
    return (
      stepfield.value &&
      ((stepfield.value instanceof Array && stepfield.value.length > 0) ||
        (!(stepfield instanceof Array) &&
          (stepfield.value as StepValueType)?.value))
    );
  }, []);

  return {
    metaFields: config.value,
    results,
    setResults,
    getTagValue,
    getFieldValue,
    resolveLinkedOptions,
    stepFieldHasValue,
  };
};
